import { MediaDeviceFailure } from './types';
import { toKey } from './devices';
import { isMediaStreamTrack } from './typeGuards';
export const createStreamTrackMap = (tracks) => {
    const streamTrackMap = new Map(tracks?.map(track => [toKey(track), track]));
    const toDeviceIndexKey = (track) => {
        const { deviceId } = track.getSettings();
        return deviceId
            ? `${track.kind}:${deviceId}`
            : undefined;
    };
    const toDeviceQueryKey = (device) => {
        switch (device.kind) {
            case 'audioinput':
                return `audio:${device.deviceId}`;
            case 'videoinput':
                return `video:${device.deviceId}`;
            default:
                return undefined;
        }
    };
    /**
     * Secondary index: `${track.kind}:${deviceId}` → tracks for O(1) device lookups.
     * Uses a Set per key so multiple tracks sharing a deviceId stay findable
     * until every one of them is removed.
     */
    const deviceIndex = new Map();
    const addToDeviceIndex = (track) => {
        const indexKey = toDeviceIndexKey(track);
        if (!indexKey) {
            return;
        }
        const existing = deviceIndex.get(indexKey);
        if (existing) {
            existing.add(track);
        }
        else {
            deviceIndex.set(indexKey, new Set([track]));
        }
    };
    const removeFromDeviceIndex = (track) => {
        const indexKey = toDeviceIndexKey(track);
        if (!indexKey) {
            return;
        }
        const existing = deviceIndex.get(indexKey);
        if (!existing) {
            return;
        }
        existing.delete(track);
        if (existing.size === 0) {
            deviceIndex.delete(indexKey);
        }
    };
    for (const track of streamTrackMap.values()) {
        addToDeviceIndex(track);
    }
    /**
     * Find track from the list of track in use.
     */
    const findTrack = (device) => {
        const queryKey = toDeviceQueryKey(device);
        if (!queryKey) {
            return undefined;
        }
        const tracks = deviceIndex.get(queryKey);
        return tracks?.values().next().value;
    };
    /**
     * Construct a `DeviceKey` from `DeviceOrTrack`
     */
    const toStreamTrackKey = (deviceOrTrack) => {
        // Unify interfaces
        const track = isMediaStreamTrack(deviceOrTrack)
            ? deviceOrTrack
            : findTrack(deviceOrTrack);
        const kind = track?.kind || 'unknown-kind';
        const id = track?.id || 'unknown-id';
        const label = track?.label || 'unknown-label';
        return toKey({ kind, id, label });
    };
    /**
     * Add the `MediaStreamTrack` to `streamTrackMap`
     */
    const add = (track) => {
        const key = toStreamTrackKey(track);
        if (key) {
            addToDeviceIndex(track);
            return streamTrackMap.set(key, track);
        }
        throw new Error(MediaDeviceFailure.StreamTrackNotFound);
    };
    /**
     * Check if `streamTrackMap` has provided track or device
     */
    const has = (deviceOrTrack) => {
        const key = toStreamTrackKey(deviceOrTrack);
        return key && streamTrackMap.has(key);
    };
    /**
     * Stop all tracks and empty `streamTrackMap`
     */
    const clear = () => {
        for (const track of streamTrackMap.values()) {
            track.stop();
        }
        streamTrackMap.clear();
        deviceIndex.clear();
    };
    /**
     * Stop the track and remove the track from `streamTrackMap`
     */
    const remove = (track) => {
        const key = toStreamTrackKey(track);
        removeFromDeviceIndex(track);
        track.stop();
        return streamTrackMap.delete(key);
    };
    /**
     * Get the size of `streamTrackMap`
     */
    const size = () => streamTrackMap.size;
    return {
        add,
        has,
        clear,
        remove,
        size,
    };
};
//# sourceMappingURL=streamTrackMap.js.map